import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Snackbar,
  CircularProgress,
  OutlinedInput,
  Chip,
  ListItemText,
  Checkbox,
  InputAdornment,
  List,
  ListItem,
  Paper,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { Key as KeyIcon } from '@mui/icons-material';
import DataTable from '../components/DataTable';
import { Role, Column, PaginationParams } from '../types';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { rolesService } from '../services/rolesService';
import { tenantService } from '../services/tenantService';
import { permissionsService } from '../services/permissionsService';
import { mapApiRoleToUi, mapUiToCreateRoleApiModel, mapUiToUpdateRoleApiModelPatch } from '../utils/rolesMapper';
import StatusChip from '../components/StatusChip';
import { PERMISSIONS } from '../utils/permissionAccess';

interface RoleFormData {
  name: string;
  description: string;
  tenant: string[];
  permissions: string[];
  status: 'Active' | 'Inactive';
}

const Roles: React.FC = () => {
  const { roles, setRoles } = useData();
  const { tokens, login, user } = useAuth();
  
  // Get user permissions
  const userPermissions = user?.permissions || [];
  
  // Helper function to check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    return userPermissions.some(userPerm => 
      userPerm.toLowerCase() === permission.toLowerCase() ||
      userPerm === permission
    );
  };
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    pageSize: 10,
  });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [tenants, setTenants] = useState<any[]>([]);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [permissionError, setPermissionError] = useState<string | null>(null);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    tenant: [],
    permissions: [],
    status: 'Active',
  });

  // Edit Tenants modal state
  const [openEditTenantsModal, setOpenEditTenantsModal] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [availableTenants, setAvailableTenants] = useState<{ id: string; name: string }[]>([]);
  const [assignedTenants, setAssignedTenants] = useState<string[]>([]);
  const [loadingTenants, setLoadingTenants] = useState<boolean>(false);
  const [loadingAssignedTenants, setLoadingAssignedTenants] = useState<boolean>(false);

  // Manage Permissions modal state
  const [openManagePermissionsModal, setOpenManagePermissionsModal] = useState<boolean>(false);
  const [selectedRoleForPermissions, setSelectedRoleForPermissions] = useState<Role | null>(null);
  const [availablePermissions, setAvailablePermissions] = useState<{ id: string; name: string; description: string }[]>([]);
  const [assignedPermissions, setAssignedPermissions] = useState<string[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState<boolean>(false);
  const [loadingAssignedPermissions, setLoadingAssignedPermissions] = useState<boolean>(false);
  const [permissionSearchTerm, setPermissionSearchTerm] = useState<string>('');

  const columns: Column[] = [
    { id: 'name', label: 'Name', minWidth: 150, width: '20%', sortable: true },
    { id: 'description', label: 'Description', minWidth: 250, width: '35%', sortable: true },
    { 
      id: 'tenant', 
      label: 'Tenant', 
      minWidth: 120, 
      width: '15%', 
      sortable: true,
      render: (value: string) => getTenantName(value),
    },
    { id: 'totalUsers', label: 'Total Users', minWidth: 120, width: '10%', sortable: true, numeric: true },
    { 
      id: 'status', 
      label: 'Status', 
      minWidth: 100, 
      width: '10%', 
      sortable: true,
      render: (value: string) => <StatusChip status={value as 'Active' | 'Inactive'} />,
    },
  ];

  // Initialize services and load data
  useEffect(() => {
    if (tokens && login) {
      rolesService.initialize(tokens, login);
      tenantService.initialize(tokens, login);
      permissionsService.initialize(tokens, login);
      loadRoles();
      loadTenants();
      loadPermissions();
    }
  }, [tokens, login]);

  // Handle pagination changes
  const handlePageChange = (newPage: number): void => {
    setPagination(prev => ({ ...prev, page: newPage }));
    loadRoles({ ...pagination, page: newPage });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    setPagination(prev => ({ ...prev, page: 1, pageSize: newPageSize }));
    loadRoles({ ...pagination, page: 1, pageSize: newPageSize });
  };

  const handleSortChange = (newSortBy: string, newSortDirection: 'asc' | 'desc'): void => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
    setPagination(prev => ({ ...prev, page: 1 }));
    loadRoles({ ...pagination, page: 1, sortBy: newSortBy, sortDirection: newSortDirection });
  };


  const loadRoles = async (params: PaginationParams = pagination) => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if roles endpoints are available
      if (!rolesService.isAuthenticated()) {
        setError('Authentication required. Please log in.');
        setLoading(false);
        return;
      }

      // Include sorting parameters in the API call
      const activeSortBy = sortBy || params.sortBy;
      const apiParams = {
        ...params,
        sortBy: activeSortBy || undefined,
        sortDirection: activeSortBy ? (sortDirection || params.sortDirection) : undefined
      };

      // Call the actual API endpoint
      const response = await rolesService.getRoles(apiParams);
      
      if (response.succeeded) {
        // response.data contains the paginated result with items array
        console.log('🔐 Raw roles data from API:', response.data);
        const rolesData = response.data?.items || [];
        const mappedRoles = rolesData.map(mapApiRoleToUi);
        console.log('🔐 Mapped roles with totalUserCount:', mappedRoles);
        
        // Extract pagination info from API response
        // API response structure: { data: { items, totalCount, pageNumber, pageSize, pageCount }, totalCount, pageNumber, pageSize, totalPages }
        const apiTotalCount = response.data?.totalCount || response.totalCount || 0;
        const apiPageCount = response.data?.pageCount || response.totalPages || 0;
        
        // Update pagination state
        setPagination(prev => ({
          ...prev,
          page: params.page || prev.page,
          pageSize: params.pageSize || prev.pageSize,
        }));
        
        // Set total items and pages from API response
        setTotalItems(apiTotalCount);
        setTotalPages(apiPageCount);
        
        // Set the roles directly since tenant info is already included in API response
        setRoles(mappedRoles);
      } else {
        setError(response.errorDescription || 'Failed to load roles');
      }
    } catch (err) {
      console.error('Error loading roles:', err);
      setError('Failed to load roles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async (): Promise<{ id: string; name: string }[]> => {
    try {
      setLoadingTenants(true);
      setTenantError(null);
      console.log('🏢 Loading tenants for form dropdown...');
      const response = await tenantService.getTenants({ page: 1, pageSize: 100 });
      console.log('🏢 Tenants API Response:', response);
      
      if (response && (response.succeeded === true || response.succeeded === undefined)) {
        console.log('🏢 Full response structure:', JSON.stringify(response, null, 2));
        
        // Handle the correct API response structure
        let tenantsData = [];
        
        if (response.data && response.data.items && Array.isArray(response.data.items)) {
          // Correct paginated response structure
          tenantsData = response.data.items;
        } else {
          console.warn('🏢 Unexpected response structure:', response);
          setTenantError('Unexpected response format from server');
          setTenants([]);
          return [];
        }
        
        console.log('🏢 Extracted Tenants Data:', tenantsData);
        
        if (Array.isArray(tenantsData) && tenantsData.length > 0) {
          // Map API tenants to the format expected by the form
          const mappedTenants = tenantsData.map((tenant: any) => ({
            id: tenant.id,
            name: tenant.name
          }));
          console.log('🏢 Mapped Tenants for form:', mappedTenants);
          setTenants(mappedTenants);
          return mappedTenants;
        } else {
          console.warn('🏢 No tenant data found or empty array');
          setTenantError('No tenant data available');
          setTenants([]);
          return [];
        }
      } else {
        console.error('🏢 Failed to load tenants:', response);
        setTenantError(response?.errorDescription || 'Failed to load tenants from server');
        setTenants([]);
        return [];
      }
    } catch (err) {
      console.error('🏢 Error loading tenants:', err);
      setTenantError('Failed to load tenants. Please check your connection and try again.');
      setTenants([]);
      return [];
    } finally {
      setLoadingTenants(false);
    }
  };

  const loadPermissions = async (): Promise<{ id: string; name: string; description: string }[]> => {
    try {
      setPermissionError(null);
      console.log('🔐 Loading permissions for form dropdown...');
      const response = await permissionsService.getPermissions({ page: 1, pageSize: 100 });
      console.log('🔐 Permissions API Response:', response);
      
      if (response && response.succeeded) {
        const permissionsData = response.data?.items || [];
        const mappedPermissions = permissionsData.map((permission: any) => ({
          id: permission.id,
          name: permission.name,
          description: permission.description
        }));
        console.log('🔐 Mapped Permissions for form:', mappedPermissions);
        setPermissions(mappedPermissions);
        return mappedPermissions;
      } else {
        console.error('🔐 Failed to load permissions:', response);
        setPermissionError(response?.errorDescription || 'Failed to load permissions from server');
        setPermissions([]);
        return [];
      }
    } catch (err) {
      console.error('🔐 Error loading permissions:', err);
      setPermissionError('Failed to load permissions. Please check your connection and try again.');
      setPermissions([]);
      return [];
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAdd = (): void => {
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      tenant: [],
      permissions: [],
      status: 'Active',
    });
    setOpenModal(true);
  };

  const handleEdit = async (role: Role): Promise<void> => {
    console.log('🔐 Editing role:', role);
    setEditingRole(role);
    
    // Load current permissions for this role
    let currentPermissions: string[] = [];
    try {
      const permissionsResponse = await rolesService.getPermissionsForRole(role.id);
      if (permissionsResponse.succeeded && permissionsResponse.data) {
        // API returns either array directly or paginated structure with items
        const permissionsData = Array.isArray(permissionsResponse.data) 
          ? permissionsResponse.data 
          : (permissionsResponse.data as any)?.items || [];
        currentPermissions = permissionsData.map((permission: { id: string }) => permission.id);
        console.log('🔐 Loaded current permissions for role:', currentPermissions);
      }
    } catch (err) {
      console.error('Error loading current permissions:', err);
    }
    
    setFormData({
      name: role.name,
      description: role.description,
      tenant: role.tenantIds || [], // Use tenant IDs from role data for form population
      permissions: currentPermissions, // Load current permissions
      status: role.status,
    });
    setOpenModal(true);
  };

  const handleDelete = async (role: Role): Promise<void> => {
    try {
      setLoading(true);
      const response = await rolesService.deleteRole(role.id);
      
      if (response.succeeded) {
        showSnackbar('Role deleted successfully', 'success');
        loadRoles(); // Reload the list
      } else {
        showSnackbar(response.errorDescription || 'Failed to delete role', 'error');
      }
    } catch (err) {
      console.error('Error deleting role:', err);
      showSnackbar('Failed to delete role. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Edit Tenants handlers
  const handleEditTenants = async (role: Role): Promise<void> => {
    setSelectedRole(role);
    setOpenEditTenantsModal(true);
    
    // Load available tenants
    await loadAvailableTenants();
    
    // Use tenant IDs from role data if available, otherwise load from API
    if (role.tenantIds && role.tenantIds.length > 0) {
      setAssignedTenants(role.tenantIds);
    } else {
      await loadAssignedTenants(role.id);
    }
  };

  const loadAvailableTenants = async (): Promise<void> => {
    try {
      setLoadingTenants(true);
      const response = await tenantService.getTenants({ page: 1, pageSize: 100 });
      
      if (response.succeeded) {
        const tenantsData = response.data?.items || [];
        const mappedTenants = tenantsData.map((tenant: any) => ({
          id: tenant.id,
          name: tenant.name
        }));
        setAvailableTenants(mappedTenants);
      }
    } catch (err) {
      console.error('Error loading tenants:', err);
    } finally {
      setLoadingTenants(false);
    }
  };

  const loadAssignedTenants = async (roleId: string): Promise<void> => {
    try {
      setLoadingAssignedTenants(true);
      console.log('🔐 Loading assigned tenants for role:', roleId);
      const response = await rolesService.getTenantsForRole(roleId);
      console.log('🔐 Assigned tenants response:', response);
      
      if (response.succeeded && response.data) {
        const assignedIds = response.data.map(item => item.tenant.id);
        console.log('🔐 Assigned tenant IDs:', assignedIds);
        setAssignedTenants(assignedIds);
      } else {
        console.log('🔐 No assigned tenants found, setting empty array');
        setAssignedTenants([]);
      }
    } catch (err) {
      console.error('Error loading assigned tenants:', err);
      setAssignedTenants([]);
    } finally {
      setLoadingAssignedTenants(false);
    }
  };

  const handleTenantSelectionChange = (event: any): void => {
    const value = event.target.value;
    setAssignedTenants(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSaveTenantAssignments = async (): Promise<void> => {
    if (!selectedRole) return;

    try {
      setLoading(true);
      
      // Get current assigned tenants from API
      const currentResponse = await rolesService.getTenantsForRole(selectedRole.id);
      console.log('🔐 Current tenants response structure:', currentResponse);
      console.log('🔐 Current tenants data:', currentResponse.data);
      
      // Extract current tenant IDs - use fallback chain to handle different response structures
      // KEEP FEATURE BRANCH FIX: item.tenant?.id || item.id || item for proper tenant removal
      const currentAssignedIds = currentResponse.succeeded && currentResponse.data 
        ? currentResponse.data.map((item: { tenant?: { id: string }; id?: string }) => {
            console.log('🔐 Tenant item structure:', item);
            return item.tenant?.id || item.id || item;
          }).filter((id): id is string => typeof id === 'string' && Boolean(id))
        : [];

      console.log('🔐 Current assigned IDs from API:', currentAssignedIds);
      console.log('🔐 Selected assigned tenants (from state):', assignedTenants);

      // Calculate add and remove arrays
      // toAdd: IDs in assignedTenants that are NOT in currentAssignedIds
      const toAdd = assignedTenants.filter(id => !currentAssignedIds.includes(id));
      // toRemove: IDs in currentAssignedIds that are NOT in assignedTenants
      const toRemove = currentAssignedIds.filter(id => !assignedTenants.includes(id));

      console.log('🔐 Calculated changes:', { 
        toAdd, 
        toRemove,
        toAddCount: toAdd.length,
        toRemoveCount: toRemove.length
      });

      // Build payload according to Swagger spec: UpdateRoleTenantsServiceModel
      // API expects: { add: string[] (nullable), remove: string[] (nullable) }
      // Both arrays contain UUID strings. Empty arrays are valid.
      const payload = {
        add: toAdd,
        remove: toRemove
      };

      console.log('🔐 Final payload being sent:', JSON.stringify(payload, null, 2));

      const response = await rolesService.updateTenantAssignments(selectedRole.id, payload);

      if (response.succeeded) {
        showSnackbar('Tenant assignments updated successfully', 'success');
        setOpenEditTenantsModal(false);
        loadRoles(); // Reload the list to show updated tenant names
      } else {
        showSnackbar(response.errorDescription || 'Failed to update tenant assignments', 'error');
      }
    } catch (err) {
      console.error('Error updating tenant assignments:', err);
      showSnackbar('Failed to update tenant assignments. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Manage Permissions handlers
  const handleManagePermissions = async (role: Role): Promise<void> => {
    console.log('🔐 Opening Manage Permissions modal for role:', role);
    setSelectedRoleForPermissions(role);
    setPermissionSearchTerm(''); // Clear search when opening modal
    setOpenManagePermissionsModal(true);
    
    // Load available permissions and current assignments
    await loadAvailablePermissions();
    await loadAssignedPermissions(role.id);
  };

  const loadAvailablePermissions = async (): Promise<void> => {
    try {
      setLoadingPermissions(true);
      const response = await permissionsService.getPermissions({ page: 1, pageSize: 100 });

      if (response.succeeded && response.data) {
        const permissionsData = response.data?.items || [];
        const mappedPermissions = permissionsData.map((permission: any) => ({
          id: permission.id,
          name: permission.name,
          description: permission.description
        }));
        setAvailablePermissions(mappedPermissions);
        console.log('🔐 Loaded available permissions:', mappedPermissions);
      }
    } catch (err) {
      console.error('Error loading available permissions:', err);
      showSnackbar('Error loading permissions', 'error');
    } finally {
      setLoadingPermissions(false);
    }
  };

  const loadAssignedPermissions = async (roleId: string): Promise<void> => {
    try {
      setLoadingAssignedPermissions(true);
      console.log('🔐 Loading assigned permissions for role:', roleId);
      const response = await rolesService.getPermissionsForRole(roleId);
      console.log('🔐 Assigned permissions response:', response);
      
      if (response.succeeded && response.data) {
        // API returns either array directly or paginated structure with items
        const permissionsData = Array.isArray(response.data) 
          ? response.data 
          : (response.data as any)?.items || [];
        const assignedIds = permissionsData.map((permission: { id: string }) => permission.id);
        console.log('🔐 Assigned permission IDs:', assignedIds);
        setAssignedPermissions(assignedIds);
      } else {
        console.log('🔐 No assigned permissions found, setting empty array');
        setAssignedPermissions([]);
      }
    } catch (err) {
      console.error('Error loading assigned permissions:', err);
      setAssignedPermissions([]);
    } finally {
      setLoadingAssignedPermissions(false);
    }
  };


  const handleSavePermissionAssignments = async (): Promise<void> => {
    if (!selectedRoleForPermissions) return;

    try {
      setLoading(true);
      
      // Get current assigned permissions
      const currentResponse = await rolesService.getPermissionsForRole(selectedRoleForPermissions.id);
      const permissionsData = currentResponse.succeeded && currentResponse.data
        ? (Array.isArray(currentResponse.data) 
            ? currentResponse.data 
            : (currentResponse.data as any)?.items || [])
        : [];
      const currentAssignedIds = permissionsData.map((permission: { id: string }) => permission.id);

      // Calculate add and remove arrays
      const toAdd = assignedPermissions.filter((id: string) => !currentAssignedIds.includes(id));
      const toRemove = currentAssignedIds.filter((id: string) => !assignedPermissions.includes(id));

      console.log('🔐 Permission assignment changes:', { toAdd, toRemove });

      const response = await rolesService.updatePermissionAssignments(selectedRoleForPermissions.id, {
        add: toAdd,
        remove: toRemove
      });

      if (response.succeeded) {
        showSnackbar('Permission assignments updated successfully', 'success');
        setOpenManagePermissionsModal(false);
        loadRoles(); // Reload the list
      } else {
        showSnackbar(response.errorDescription || 'Failed to update permission assignments', 'error');
      }
    } catch (err) {
      console.error('Error updating permission assignments:', err);
      showSnackbar('Failed to update permission assignments. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (role: Role): Promise<void> => {
    try {
      setLoading(true);
      const isActive = role.status === 'Active';
      const response = await rolesService.toggleRoleStatus(role.id, !isActive);
      
      if (response.succeeded) {
        showSnackbar(`Role ${!isActive ? 'enabled' : 'disabled'} successfully`, 'success');
        loadRoles(); // Reload the list
      } else {
        showSnackbar(response.errorDescription || 'Failed to toggle role status', 'error');
      }
    } catch (err) {
      console.error('Error toggling role status:', err);
      showSnackbar('Failed to toggle role status. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    try {
      console.log('🔐 Saving role with editingRole:', editingRole);
      console.log('🔐 Form data:', formData);
      
      if (editingRole) {
        // Update existing role - use PATCH with only changed fields
        const updateData = mapUiToUpdateRoleApiModelPatch(editingRole, {
          name: formData.name,
          description: formData.description,
          status: formData.status,
        });
        
        // Check if there are any changes to role properties (name, description, status)
        const hasRoleChanges = Object.keys(updateData).length > 0;
        
        // Get current tenant IDs for comparison - use the initial state from editingRole
        // which was set when the modal was opened
        const currentTenantIds = editingRole.tenantIds || [];
        // Normalize arrays by converting to strings and sorting for comparison
        const normalizedFormTenants = formData.tenant.map(String).sort();
        const normalizedCurrentTenants = currentTenantIds.map(String).sort();
        const hasTenantChanges = JSON.stringify(normalizedFormTenants) !== JSON.stringify(normalizedCurrentTenants);
        
        // Get current permissions for comparison
        let currentPermissionIds: string[] = [];
        try {
          const permissionsResponse = await rolesService.getPermissionsForRole(editingRole.id);
          if (permissionsResponse.succeeded && permissionsResponse.data) {
            // API returns either array directly or paginated structure with items
            const permissionsData = Array.isArray(permissionsResponse.data) 
              ? permissionsResponse.data 
              : (permissionsResponse.data as any)?.items || [];
            currentPermissionIds = permissionsData.map((permission: { id: string }) => permission.id);
          }
        } catch (err) {
          console.error('Error loading current permissions for comparison:', err);
        }
        const normalizedFormPermissions = [...formData.permissions].sort();
        const normalizedCurrentPermissions = [...currentPermissionIds].sort();
        const hasPermissionChanges = JSON.stringify(normalizedFormPermissions) !== JSON.stringify(normalizedCurrentPermissions);
        
        // Check if there are any changes at all
        if (!hasRoleChanges && !hasTenantChanges && !hasPermissionChanges) {
          showSnackbar('No changes detected', 'info');
          setOpenModal(false);
          return;
        }
        
        // Determine which endpoints to call based on what changed
        // Strategy:
        // 1. If ONLY tenants changed: call only /roles/{id}/tenants endpoint
        // 2. If role properties (name, description, status) changed: call /roles/{id} endpoint
        // 3. If permissions changed: call /roles/{id}/permissions endpoint
        // 4. If multiple fields changed: call all relevant endpoints
        
        const onlyTenantsChanged = hasTenantChanges && !hasRoleChanges && !hasPermissionChanges;
        
        console.log('🔐 Update strategy:', {
          hasRoleChanges,
          hasTenantChanges,
          hasPermissionChanges,
          onlyTenantsChanged,
          willCallRoleEndpoint: hasRoleChanges,
          willCallPermissionsEndpoint: hasPermissionChanges,
          willCallTenantsEndpoint: hasTenantChanges
        });
        
        // Handle tenant changes FIRST if ONLY tenants changed
        // This allows tenant-only updates to work independently
        if (onlyTenantsChanged) {
          try {
            if (!editingRole?.id) {
              console.error('Editing role ID is undefined');
              showSnackbar('Cannot update tenant assignments: Role ID is missing', 'error');
              return;
            }

            // Get current assigned tenants - use the initial state from editingRole for consistency
            // This ensures we're comparing against what was loaded when the modal opened
            const initialTenantIds = (editingRole.tenantIds || []).map(String);
            const formTenantIds = formData.tenant.map(String);

            // Calculate tenant changes - compare form data with initial state
            const toAdd = formTenantIds.filter(id => !initialTenantIds.includes(id));
            const toRemove = initialTenantIds.filter(id => !formTenantIds.includes(id));

            console.log('🔐 Tenant-only update - change calculation:', {
              formDataTenant: formData.tenant,
              initialTenantIds: initialTenantIds,
              formTenantIds: formTenantIds,
              toAdd: toAdd,
              toRemove: toRemove
            });

            if (toAdd.length > 0 || toRemove.length > 0) {
              console.log('🔐 Updating tenant assignments (tenant-only):', { toAdd, toRemove, roleId: editingRole.id });
              
              const validToAdd = toAdd.filter(id => typeof id === 'string' && id.trim() !== '');
              const validToRemove = toRemove.filter(id => typeof id === 'string' && id.trim() !== '');
              
              const tenantResponse = await rolesService.updateTenantAssignments(editingRole.id, {
                add: validToAdd,
                remove: validToRemove
              });
              
              if (!tenantResponse.succeeded) {
                showSnackbar(tenantResponse.errorDescription || 'Failed to update tenant assignments', 'error');
                return;
              }
              
              showSnackbar('Tenant assignments updated successfully', 'success');
              loadRoles();
              setOpenModal(false);
              return; // Exit early since we only updated tenants
            }
          } catch (tenantErr) {
            console.error('Error updating tenant assignments:', tenantErr);
            showSnackbar('Failed to update tenant assignments. Please try again.', 'error');
            return;
          }
        }
        
        // Update role properties (name, description, status) if changed
        if (hasRoleChanges) {
          console.log('🔐 Sending role property update with data:', updateData);
          const response = await rolesService.updateRole(editingRole.id, updateData);
          
          if (!response.succeeded) {
            showSnackbar(response.errorDescription || 'Failed to update role', 'error');
            return;
          }
        }
        
        // Handle permission changes
        if (hasPermissionChanges) {
          try {
            if (!editingRole?.id) {
              console.error('Editing role ID is undefined');
              showSnackbar('Cannot update permission assignments: Role ID is missing', 'error');
              return;
            }

            const toAdd = formData.permissions.filter(id => !currentPermissionIds.includes(id));
            const toRemove = currentPermissionIds.filter(id => !formData.permissions.includes(id));

            console.log('🔐 Permission change calculation:', {
              formDataPermissions: formData.permissions,
              currentPermissionIds: currentPermissionIds,
              toAdd: toAdd,
              toRemove: toRemove
            });

            if (toAdd.length > 0 || toRemove.length > 0) {
              console.log('🔐 Updating permission assignments:', { toAdd, toRemove, roleId: editingRole.id });
              
              const validToAdd = toAdd.filter(id => typeof id === 'string' && id.trim() !== '');
              const validToRemove = toRemove.filter(id => typeof id === 'string' && id.trim() !== '');
              
              await rolesService.updatePermissionAssignments(editingRole.id, {
                add: validToAdd,
                remove: validToRemove
              });
            }
          } catch (permissionErr) {
            console.error('Error updating permission assignments:', permissionErr);
            showSnackbar('Role updated but failed to update permission assignment. Please check permission assignments manually.', 'warning');
          }
        }
        
        // Handle tenant changes when OTHER fields also changed
        // This endpoint works independently and should be called even when other fields changed
        if (hasTenantChanges && !onlyTenantsChanged) {
          try {
            if (!editingRole?.id) {
              console.error('Editing role ID is undefined');
              showSnackbar('Cannot update tenant assignments: Role ID is missing', 'error');
              return;
            }

            // Get current assigned tenants - use the initial state from editingRole for consistency
            // This ensures we're comparing against what was loaded when the modal opened
            const initialTenantIds = (editingRole.tenantIds || []).map(String);
            const formTenantIds = formData.tenant.map(String);

            // Calculate tenant changes - compare form data with initial state
            const toAdd = formTenantIds.filter(id => !initialTenantIds.includes(id));
            const toRemove = initialTenantIds.filter(id => !formTenantIds.includes(id));

            console.log('🔐 Tenant change calculation:', {
              formDataTenant: formData.tenant,
              initialTenantIds: initialTenantIds,
              formTenantIds: formTenantIds,
              toAdd: toAdd,
              toRemove: toRemove
            });

            if (toAdd.length > 0 || toRemove.length > 0) {
              console.log('🔐 Updating tenant assignments:', { toAdd, toRemove, roleId: editingRole.id });
              
              // Validate that all IDs are strings and not empty
              const validToAdd = toAdd.filter(id => typeof id === 'string' && id.trim() !== '');
              const validToRemove = toRemove.filter(id => typeof id === 'string' && id.trim() !== '');
              
              console.log('🔐 Validated tenant assignments:', { 
                validToAdd, 
                validToRemove, 
                originalToAdd: toAdd, 
                originalToRemove: toRemove 
              });
              
              const tenantResponse = await rolesService.updateTenantAssignments(editingRole.id, {
                add: validToAdd,
                remove: validToRemove
              });
              
              if (!tenantResponse.succeeded) {
                showSnackbar(tenantResponse.errorDescription || 'Failed to update tenant assignments', 'error');
                return;
              }
            }
          } catch (tenantErr) {
            console.error('Error updating tenant assignments:', tenantErr);
            showSnackbar('Role properties updated but failed to update tenant assignments. Please check tenant assignments manually.', 'warning');
          }
        }

        showSnackbar('Role updated successfully', 'success');
        loadRoles(); // Reload the list
        setOpenModal(false);
      } else {
        // Create new role
        const createData = mapUiToCreateRoleApiModel({
          name: formData.name,
          description: formData.description,
          tenant: formData.tenant,
          permissions: formData.permissions,
        });
        const response = await rolesService.createRole(createData);
        
        if (response.succeeded) {
          showSnackbar('Role created successfully', 'success');
          loadRoles(); // Reload the list
          setOpenModal(false);
        } else {
          showSnackbar(response.errorDescription || 'Failed to create role', 'error');
        }
      }
    } catch (err) {
      console.error('Error saving role:', err);
      showSnackbar('Failed to save role. Please try again.', 'error');
    }
  };

  const handleCancel = (): void => {
    setOpenModal(false);
  };

  // Get tenant name for display (fallback to ID if name not found)
  const getTenantName = (tenantId: string): string => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.name : tenantId;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom>
        Manage Roles
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTable
          data={roles}
          columns={columns}
          onAdd={hasPermission(PERMISSIONS.CREATE_ROLE) ? handleAdd : undefined}
          onEdit={hasPermission(PERMISSIONS.EDIT_ROLE) ? handleEdit : undefined}
          onDelete={hasPermission(PERMISSIONS.DELETE_ROLE) ? handleDelete : undefined}
          onToggleStatus={hasPermission(PERMISSIONS.EDIT_ROLE) ? handleToggleStatus : undefined}
          onEditTenants={hasPermission(PERMISSIONS.EDIT_ROLE_TENANT_ASSOCIATION) ? handleEditTenants : undefined}
          onManagePermissions={hasPermission(PERMISSIONS.CHANGE_PERMISSIONS_ASSOCIATION) ? handleManagePermissions : undefined}
          title="Roles"
          searchFields={['name', 'description', 'tenant']}
          isRowDisabled={(row) => row.isDefault === true}
          disabledRowTooltip="Default roles cannot be modified"
          pagination={{
            page: pagination.page || 1,
            pageSize: pagination.pageSize || 10,
            totalItems,
            totalPages,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
            onSortChange: handleSortChange,
          }}
        />
      )}

      <Dialog open={openModal} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRole ? 'Edit Role' : 'Add New Role'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Role Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Administrator, User Manager"
            />
            
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              required
              multiline
              rows={3}
              placeholder="Describe the role's responsibilities and permissions"
            />
            
            <FormControl fullWidth required error={!!tenantError}>
              <InputLabel>Tenants</InputLabel>
              <Select
                multiple
                value={formData.tenant}
                onChange={(e) => setFormData({ ...formData, tenant: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
                input={<OutlinedInput label="Tenants" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const tenant = tenants.find(t => t.id === value);
                      return (
                        <Chip 
                          key={value} 
                          label={tenant?.name || value} 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>
                )}
                disabled={loadingTenants || !!tenantError}
              >
                {loadingTenants ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading tenants...
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : tenantError ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="error">
                      {tenantError}
                    </Typography>
                  </MenuItem>
                ) : tenants.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No tenants available
                    </Typography>
                  </MenuItem>
                ) : (
                  tenants.map((tenant) => (
                    <MenuItem key={tenant.id} value={tenant.id}>
                      <Checkbox checked={formData.tenant.indexOf(tenant.id) > -1} />
                      <ListItemText primary={tenant.name} />
                    </MenuItem>
                  ))
                )}
              </Select>
              {tenantError && (
                <FormHelperText error>
                  {tenantError}
                </FormHelperText>
              )}
            </FormControl>
            
            <FormControl fullWidth error={!!permissionError}>
              <InputLabel>Permissions</InputLabel>
              <Select
                multiple
                value={formData.permissions}
                onChange={(e) => setFormData({ ...formData, permissions: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
                input={<OutlinedInput label="Permissions" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const permission = permissions.find(p => p.id === value);
                      return (
                        <Chip 
                          key={value} 
                          label={permission?.name || value} 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>
                )}
                disabled={!!permissionError}
              >
                {permissionError ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="error">
                      {permissionError}
                    </Typography>
                  </MenuItem>
                ) : permissions.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No permissions available
                    </Typography>
                  </MenuItem>
                ) : (
                  permissions.map((permission) => (
                    <MenuItem key={permission.id} value={permission.id}>
                      <Checkbox checked={formData.permissions.indexOf(permission.id) > -1} />
                      <ListItemText 
                        primary={permission.name} 
                        secondary={permission.description}
                      />
                    </MenuItem>
                  ))
                )}
              </Select>
              {permissionError && (
                <FormHelperText error>
                  {permissionError}
                </FormHelperText>
              )}
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as RoleFormData['status'] })}
                label="Status"
                renderValue={(value) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status={value as 'Active' | 'Inactive'} size="small" />
                  </Box>
                )}
              >
                <MenuItem value="Active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status="Active" size="small" />
                    <span>Active</span>
                  </Box>
                </MenuItem>
                <MenuItem value="Inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status="Inactive" size="small" />
                    <span>Inactive</span>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tenants Modal */}
      <Dialog open={openEditTenantsModal} onClose={() => setOpenEditTenantsModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Tenants for {selectedRole?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the tenants that should be assigned to this role. You can select multiple tenants.
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Assigned Tenants</InputLabel>
              <Select
                multiple
                value={assignedTenants}
                onChange={handleTenantSelectionChange}
                input={<OutlinedInput label="Assigned Tenants" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const tenant = availableTenants.find(t => t.id === value);
                      return (
                        <Chip 
                          key={value} 
                          label={tenant?.name || value} 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>
                )}
                disabled={loadingTenants || loadingAssignedTenants}
              >
                {loadingTenants || loadingAssignedTenants ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <span>{loadingTenants ? 'Loading tenants...' : 'Loading assignments...'}</span>
                    </Box>
                  </MenuItem>
                ) : (
                  availableTenants.map((tenant) => (
                    <MenuItem key={tenant.id} value={tenant.id}>
                      <Checkbox checked={assignedTenants.indexOf(tenant.id) > -1} />
                      <ListItemText primary={tenant.name} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {assignedTenants.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Selected tenants: {assignedTenants.length}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditTenantsModal(false)} color="secondary">
            Cancel
          </Button>
          <Button 
            onClick={handleSaveTenantAssignments} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Save Assignments'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Permissions Modal */}
      <Dialog 
        open={openManagePermissionsModal} 
        onClose={() => {
          setOpenManagePermissionsModal(false);
          setPermissionSearchTerm(''); // Clear search when closing modal
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <KeyIcon sx={{ color: '#ff9800' }} />
            <span>Manage Permissions - {selectedRoleForPermissions?.name}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the permissions that should be assigned to this role. You can select multiple permissions.
            </Typography>
            
            <TextField
              label="Search Permissions"
              value={permissionSearchTerm}
              onChange={(e) => setPermissionSearchTerm(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Search by name or description..."
            />
            
            {loadingPermissions || loadingAssignedPermissions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  {loadingPermissions ? 'Loading permissions...' : 'Loading assignments...'}
                </Typography>
              </Box>
            ) : (() => {
              const filteredPermissions = availablePermissions.filter((permission) => {
                if (!permissionSearchTerm.trim()) return true;
                const searchLower = permissionSearchTerm.toLowerCase();
                return (
                  permission.name.toLowerCase().includes(searchLower) ||
                  permission.description?.toLowerCase().includes(searchLower)
                );
              });
              
              return (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {filteredPermissions.length} permission{filteredPermissions.length !== 1 ? 's' : ''} available
                      {permissionSearchTerm && ` (filtered from ${availablePermissions.length})`}
                    </Typography>
                    <Typography variant="body2" color="primary">
                      {assignedPermissions.length} selected
                    </Typography>
                  </Box>
                  
                  {filteredPermissions.length === 0 ? (
                    <Paper sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No permissions found matching "{permissionSearchTerm}"
                      </Typography>
                    </Paper>
                  ) : (
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        maxHeight: 400, 
                        overflow: 'auto',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <List dense>
                        {filteredPermissions.map((permission) => {
                          const isSelected = assignedPermissions.indexOf(permission.id) > -1;
                          return (
                            <ListItem
                              key={permission.id}
                              button
                              onClick={() => {
                                if (isSelected) {
                                  setAssignedPermissions(assignedPermissions.filter(id => id !== permission.id));
                                } else {
                                  setAssignedPermissions([...assignedPermissions, permission.id]);
                                }
                              }}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                                backgroundColor: isSelected ? 'action.selected' : 'transparent',
                              }}
                            >
                              <Checkbox 
                                checked={isSelected}
                                tabIndex={-1}
                                disableRipple
                              />
                              <ListItemText 
                                primary={permission.name} 
                                secondary={permission.description}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Paper>
                  )}
                </Box>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenManagePermissionsModal(false);
            setPermissionSearchTerm(''); // Clear search when canceling
          }} color="secondary">
            Cancel
          </Button>
          <Button 
            onClick={handleSavePermissionAssignments} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Save Assignments'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Roles; 